/**
 * RewardsTab — Mini-orchestrator for the "Récompenses" main tab.
 *
 * Contains 3 sub-sections rendered via a pill-style sub-nav:
 *   0 → Types de Coupon   (/admin/coupon-types)
 *   1 → Catégories        (/admin/reward-categories)
 *   2 → Récompenses       (/rewards)
 *
 * All data & CRUD state is lifted from parent (index.jsx) via props.
 */
import React, { useState } from "react";
import {
    Box, Button, Tabs, Tab, Typography, Divider,
} from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CategoryIcon from "@mui/icons-material/Category";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import AddIcon from "@mui/icons-material/Add";

import { SECTION_COLORS } from "../../constants";

// ── CouponTypes sub-section ─────────────────────────────────
import CouponTypesSection from "./couponTypes/CouponTypesSection";
import CreateCouponTypeDialog from "./couponTypes/CreateCouponTypeDialog";
import EditCouponTypeDialog from "./couponTypes/EditCouponTypeDialog";
import ViewCouponTypeDialog from "./couponTypes/ViewCouponTypeDialog";
import DeleteCouponTypeDialog from "./couponTypes/DeleteCouponTypeDialog";

// ── RewardCategories sub-section ────────────────────────────
import RewardCategoriesSection from "./rewardCategories/RewardCategoriesSection";
import CreateRewardCategoryDialog from "./rewardCategories/CreateRewardCategoryDialog";
import EditRewardCategoryDialog from "./rewardCategories/EditRewardCategoryDialog";
import ViewRewardCategoryDialog from "./rewardCategories/ViewRewardCategoryDialog";
import DeleteRewardCategoryDialog from "./rewardCategories/DeleteRewardCategoryDialog";

// ── Rewards sub-section ─────────────────────────────────────
import RewardsListSection from "./RewardsListSection";
import CreateRewardDialog from "./CreateRewardDialog";
import EditRewardDialog from "./EditRewardDialog";
import ViewRewardDialog from "./ViewRewardDialog";
import DeleteRewardDialog from "./DeleteRewardDialog";

const SUB_TABS = [
    { label: "Types de Coupon", icon: <ConfirmationNumberIcon sx={{ fontSize: 15 }} /> },
    { label: "Catégories", icon: <CategoryIcon sx={{ fontSize: 15 }} /> },
    { label: "Récompenses", icon: <CardGiftcardIcon sx={{ fontSize: 15 }} /> },
];

const RewardsTab = ({
    // APIs (data, loading, error, refetch)
    couponTypesApi,
    rewardCategoriesApi,
    rewardsApi,
    // CRUD callbacks lifted to parent
    onDeleteCouponTypeConfirmed,
    deletingCouponType,
    onDeleteRewardCategoryConfirmed,
    deletingRewardCategory,
    onDeleteRewardConfirmed,
    deletingReward,
    // notify
    notify,
}) => {
    const [subTab, setSubTab] = useState(0);

    // ── CouponType local dialog state ────────────────────────
    const [createCouponTypeOpen, setCreateCouponTypeOpen] = useState(false);
    const [viewCouponType, setViewCouponType] = useState(null);
    const [editCouponType, setEditCouponType] = useState(null);
    const [deleteCouponType, setDeleteCouponType] = useState(null);

    // ── RewardCategory local dialog state ────────────────────
    const [createRewardCategoryOpen, setCreateRewardCategoryOpen] = useState(false);
    const [viewRewardCategory, setViewRewardCategory] = useState(null);
    const [editRewardCategory, setEditRewardCategory] = useState(null);
    const [deleteRewardCategory, setDeleteRewardCategory] = useState(null);

    // ── Reward local dialog state ────────────────────────────
    const [createRewardOpen, setCreateRewardOpen] = useState(false);
    const [viewReward, setViewReward] = useState(null);
    const [editReward, setEditReward] = useState(null);
    const [deleteReward, setDeleteReward] = useState(null);

    // ── Pagination per sub-tab ───────────────────────────────
    const [pages, setPages] = useState([0, 0, 0]);
    const handlePageChange = (idx) => (_, newPage) =>
        setPages((prev) => { const n = [...prev]; n[idx] = newPage; return n; });

    // ── "Add" button per sub-tab ─────────────────────────────
    const addActions = [
        { label: "Ajouter un Type de Coupon", action: () => setCreateCouponTypeOpen(true) },
        { label: "Ajouter une Catégorie", action: () => setCreateRewardCategoryOpen(true) },
        { label: "Ajouter une Récompense", action: () => setCreateRewardOpen(true) },
    ];

    const couponTypesList = Array.isArray(couponTypesApi?.data) ? couponTypesApi.data : [];
    const rewardCategoriesList = Array.isArray(rewardCategoriesApi?.data) ? rewardCategoriesApi.data : [];

    return (
        <>
            {/* ── CouponType Dialogs ─────────────────────────────────── */}
            <CreateCouponTypeDialog
                open={createCouponTypeOpen}
                onClose={() => setCreateCouponTypeOpen(false)}
                onCreated={() => { couponTypesApi.refetch(); notify("Type de coupon créé avec succès"); }}
            />
            <ViewCouponTypeDialog
                open={Boolean(viewCouponType)} couponType={viewCouponType}
                onClose={() => setViewCouponType(null)} onEditRequest={setEditCouponType}
            />
            <EditCouponTypeDialog
                open={Boolean(editCouponType)} couponType={editCouponType}
                onClose={() => setEditCouponType(null)}
                onSaved={() => { couponTypesApi.refetch(); notify("Type de coupon mis à jour avec succès"); }}
            />
            <DeleteCouponTypeDialog
                open={Boolean(deleteCouponType)} couponType={deleteCouponType}
                onClose={() => !deletingCouponType && setDeleteCouponType(null)}
                onConfirm={() => onDeleteCouponTypeConfirmed(deleteCouponType, setDeleteCouponType)}
                deleting={deletingCouponType}
            />

            {/* ── RewardCategory Dialogs ─────────────────────────────── */}
            <CreateRewardCategoryDialog
                open={createRewardCategoryOpen}
                onClose={() => setCreateRewardCategoryOpen(false)}
                onCreated={() => { rewardCategoriesApi.refetch(); notify("Catégorie créée avec succès"); }}
                couponTypes={couponTypesList}
            />
            <ViewRewardCategoryDialog
                open={Boolean(viewRewardCategory)} rewardCategory={viewRewardCategory}
                onClose={() => setViewRewardCategory(null)} onEditRequest={setEditRewardCategory}
                couponTypes={couponTypesList}
            />
            <EditRewardCategoryDialog
                open={Boolean(editRewardCategory)} rewardCategory={editRewardCategory}
                onClose={() => setEditRewardCategory(null)}
                onSaved={() => { rewardCategoriesApi.refetch(); notify("Catégorie mise à jour avec succès"); }}
                couponTypes={couponTypesList}
            />
            <DeleteRewardCategoryDialog
                open={Boolean(deleteRewardCategory)} rewardCategory={deleteRewardCategory}
                onClose={() => !deletingRewardCategory && setDeleteRewardCategory(null)}
                onConfirm={() => onDeleteRewardCategoryConfirmed(deleteRewardCategory, setDeleteRewardCategory)}
                deleting={deletingRewardCategory}
            />

            {/* ── Reward Dialogs ─────────────────────────────────────── */}
            <CreateRewardDialog
                open={createRewardOpen}
                onClose={() => setCreateRewardOpen(false)}
                onCreated={() => { rewardsApi.refetch(); notify("Récompense créée avec succès"); }}
                rewardCategories={rewardCategoriesList}
            />
            <ViewRewardDialog
                open={Boolean(viewReward)} reward={viewReward}
                onClose={() => setViewReward(null)} onEditRequest={setEditReward}
                rewardCategories={rewardCategoriesList}
            />
            <EditRewardDialog
                open={Boolean(editReward)} reward={editReward}
                onClose={() => setEditReward(null)}
                onSaved={() => { rewardsApi.refetch(); notify("Récompense mise à jour avec succès"); }}
                rewardCategories={rewardCategoriesList}
            />
            <DeleteRewardDialog
                open={Boolean(deleteReward)} reward={deleteReward}
                onClose={() => !deletingReward && setDeleteReward(null)}
                onConfirm={() => onDeleteRewardConfirmed(deleteReward, setDeleteReward)}
                deleting={deletingReward}
            />

            {/* ── Sub-nav + "Add" button ────────────────────────────── */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <Tabs
                    value={subTab}
                    onChange={(_, v) => setSubTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 36,
                        "& .MuiTabs-flexContainer": { gap: 0.5 },
                        "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 600,
                            fontFamily: "Montserrat, sans-serif",
                            fontSize: 12,
                            minHeight: 36,
                            borderRadius: 2,
                            px: 1.5,
                            color: "#64748b",
                            transition: "all 0.2s",
                            "&:hover": { bgcolor: `${SECTION_COLORS.rewards.main}10`, color: SECTION_COLORS.rewards.main },
                        },
                        "& .Mui-selected": {
                            color: `${SECTION_COLORS.rewards.main} !important`,
                            bgcolor: `${SECTION_COLORS.rewards.main}12`,
                        },
                        "& .MuiTabs-indicator": {
                            height: 2,
                            borderRadius: "2px 2px 0 0",
                            background: SECTION_COLORS.rewards.gradient,
                        },
                    }}
                >
                    {SUB_TABS.map((t, idx) => (
                        <Tab
                            key={idx}
                            label={
                                <Box display="flex" alignItems="center" gap={0.6}>
                                    {t.icon}
                                    {t.label}
                                </Box>
                            }
                        />
                    ))}
                </Tabs>

                <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addActions[subTab].action}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: 12,
                        borderRadius: 2,
                        background: SECTION_COLORS.rewards.gradient,
                        boxShadow: `0 2px 8px ${SECTION_COLORS.rewards.main}30`,
                        "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.rewards.main}40` },
                    }}
                >
                    {addActions[subTab].label}
                </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* ── Sub-section content ───────────────────────────────── */}
            {subTab === 0 && (
                <CouponTypesSection
                    couponTypesApi={couponTypesApi}
                    page={pages[0]}
                    onPageChange={handlePageChange(0)}
                    onView={setViewCouponType}
                    onEdit={setEditCouponType}
                    onDelete={setDeleteCouponType}
                />
            )}
            {subTab === 1 && (
                <RewardCategoriesSection
                    rewardCategoriesApi={rewardCategoriesApi}
                    couponTypesApi={couponTypesApi}
                    page={pages[1]}
                    onPageChange={handlePageChange(1)}
                    onView={setViewRewardCategory}
                    onEdit={setEditRewardCategory}
                    onDelete={setDeleteRewardCategory}
                />
            )}
            {subTab === 2 && (
                <RewardsListSection
                    rewardsApi={rewardsApi}
                    rewardCategories={rewardCategoriesList}
                    page={pages[2]}
                    onPageChange={handlePageChange(2)}
                    onView={setViewReward}
                    onEdit={setEditReward}
                    onDelete={setDeleteReward}
                />
            )}
        </>
    );
};

export default RewardsTab;
